import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, ElementRef, importProvidersFrom, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import {
  Dropdown,
  DropdownInterface,
  DropdownOptions,
  InstanceOptions,
  Modal,
  ModalInterface,
  ModalOptions,
  TabItem,
  Tabs,
  TabsInterface,
  TabsOptions,
} from 'flowbite';
import { CookieService } from 'ngx-cookie-service';
import { BrowserService } from 'src/app/core/browser.service';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import { AppStateInterface } from 'src/app/store/types';
import { AsyncPipe, isPlatformBrowser, NgIf } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { PageService } from 'src/app/core/page/page.service';
import { httpLoaderFactory } from 'src/app/page/page.config';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { setActiveLanguage } from 'src/app/store/language/actions';
import { provideAnimations } from '@angular/platform-browser/animations';

@Component({
    selector: 'app-cookie-consent',
    templateUrl: './cookie-consent.component.html',
    styleUrl: './cookie-consent.component.scss',
    animations: [
        trigger('animateMoadl', [
            state('false', style({
                opacity: 0,
                transform: 'translateY(10%)',
            })),
            state('true', style({
                opacity: 1,
                transform: 'translateY(0%)',
            })),
            transition('false => true', animate('300ms ease-in')),
        ]),
    ],
    imports: [
        AsyncPipe,
        ReactiveFormsModule,
        TranslateModule,
    ],
    providers: [
        {
            provide: TranslateLoader,
            useFactory: httpLoaderFactory,
            deps: [HttpClient],
        },
    ]
})
export class CookieConsentComponent {
  @ViewChild('cookieConsdentModal', { static: false })
  cookieConsdentModalElement!: ElementRef;
  @ViewChild('languageDropdownButton', { static: false })
  languageDropdownButton!: ElementRef;
  @ViewChild('languageDropdownMenu', { static: false })
  languageDropdownMenu!: ElementRef;

  @ViewChild('tabs', { static: false }) tabsElement!: ElementRef;
  @ViewChild('approvalTab', { static: false }) approvalTab!: ElementRef;
  @ViewChild('approvalTabContent', { static: false })
  approvalTabContent!: ElementRef;
  @ViewChild('detailsTab', { static: false }) detailsTab!: ElementRef;
  @ViewChild('detailsTabContent', { static: false })
  detailsTabContent!: ElementRef;
  @ViewChild('aboutCookiesTab', { static: false }) aboutCookiesTab!: ElementRef;
  @ViewChild('aboutCookiesTabContent', { static: false })
  aboutCookiesTabContent!: ElementRef;

  tabs!: TabsInterface;

  cookieConsdentModal!: ModalInterface;
  animateState: boolean = false;

    activeLanguage: BehaviorSubject<string | null> = new BehaviorSubject<
      string | null
    >(null);
  languageDirection: string = 'ltr'

  cookieSettingsForm = new FormGroup({
    statistik: new FormControl('', Validators.required),
    marketing: new FormControl('', Validators.required),
  });

  constructor(
    private _cookieService: CookieService,
    private _translate: TranslateService,
    private readonly browserService: BrowserService,
    private _router: Router,
    private _store: Store<AppStateInterface>
  ) {
  }

  ngOnInit() {
    const urlFragments = this._router.url.split('/');
    if(urlFragments && urlFragments.length > 1){
      const activeLanguage = urlFragments[1];
      this.activeLanguage.next(activeLanguage);
      this._translate.setDefaultLang(activeLanguage);
      this._translate.use(activeLanguage);
    }
  }

  ngAfterViewInit() {
    if(this.browserService.isBrowserPlatform()){
      this.initTabsElement(this.tabsElement.nativeElement);
      this.addEventListenerToDropdown(
        this.languageDropdownButton.nativeElement,
        this.languageDropdownMenu.nativeElement,
      );
      const savedConsent = this._cookieService.get('userConsent');
      if (!savedConsent) {
        setTimeout(() => {
          this.initCookieConsentModalElement(
            this.cookieConsdentModalElement.nativeElement,
          );
          this.cookieConsdentModal.show();
        }, 3000);
      }
    }

  }

  private initCookieConsentModalElement(element: any): void {
    const modalOptions: ModalOptions = {
      placement: 'bottom-right',
      backdrop: 'static',
      backdropClasses: 'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40',
      closable: true,
      onHide: () => {
        this.animateState = false;
      },
      onShow: () => {
        this.animateState = true;
      },
      onToggle: () => {},
    };
    const instanceOptions = {
      id: 'modalDialog',
      override: true,
    };
    this.cookieConsdentModal = new Modal(
      element,
      modalOptions,
      instanceOptions,
    );
  }

  private initTabsElement(element: any) {
    const tabsElement: HTMLElement = element;
    const tabElements: TabItem[] = [
      {
        id: 'approval',
        triggerEl: this.approvalTab.nativeElement,
        targetEl: this.approvalTabContent.nativeElement,
      },
      {
        id: 'details',
        triggerEl: this.detailsTab.nativeElement,
        targetEl: this.detailsTabContent.nativeElement,
      },
      {
        id: 'aboutCookies',
        triggerEl: this.aboutCookiesTab.nativeElement,
        targetEl: this.aboutCookiesTabContent.nativeElement,
      },
    ];

    const options: TabsOptions = {
      defaultTabId: 'approval',
      activeClasses:
        'text-blue-600 scale-105 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-400 border-blue-600 dark:border-blue-500',
      inactiveClasses:
        'text-gray-500 hover:text-gray-600 dark:text-gray-400 border-gray-100 hover:border-gray-300 dark:border-gray-700 dark:hover:text-gray-300',
      onShow: () => {},
    };

    const instanceOptions: InstanceOptions = {
      id: 'tabs',
      override: true,
    };
    this.tabs = new Tabs(tabsElement, tabElements, options, instanceOptions);
    this.tabs.show('approval');
  }

  closeCookieConsdentModal() {
    this.cookieConsdentModal.hide();
  }

  setLanguage(newLanguage: string) {
    this.activeLanguage.next(newLanguage);
    this._translate.use(newLanguage);

    const browserDocument = this.browserService.document;
    if(browserDocument){
      if (newLanguage === 'fa') {
        const elements = browserDocument.querySelectorAll('[dir="ltr"]');
        elements.forEach((element) => {
          element.setAttribute('dir', 'rtl');
        });
      } else {
        const elements = browserDocument.querySelectorAll('[dir="rtl"]');
        elements.forEach((element) => {
          element.setAttribute('dir', 'ltr');
        });
      }
    }
    this._store.dispatch(setActiveLanguage({activeLanguage: newLanguage}))
  }

  onFormSubmit() {
    this.acceptAll();
  }

  acceptAll() {
    const consentObject = {
      essentials: true,
      statistics: true,
      marketing: true,
    };
    const encodedObject = this.encodeURL(consentObject);
    this._cookieService.set('userConsent', encodedObject);
    this.closeCookieConsdentModal();
  }

  acceptEssentials() {
    const consentObject = {
      essentials: true,
    };
    const encodedObject = this.encodeURL(consentObject);
    this._cookieService.set('userConsent', encodedObject);
    this.closeCookieConsdentModal();
  }

  goToSettings() {
    this.tabs.show('details');
  }

  private addEventListenerToDropdown(
    nativeElementButton: HTMLElement,
    nativeElementMenu: HTMLElement,
  ): void {
    const options: DropdownOptions = {
      placement: 'bottom',
      triggerType: 'click',
      offsetSkidding: 0,
      offsetDistance: 10,
      delay: 0,
    };

    const dropdown: DropdownInterface = new Dropdown(
      nativeElementMenu,
      nativeElementButton,
      options,
    );

    nativeElementMenu.addEventListener('click', (e: any) => {
      dropdown.hide();
    });
  }

  // TODO: Remove this method if not needed
  private decodeURL(urlString: string) {
    const decodedString = decodeURIComponent(urlString);
    const jsonObject = JSON.parse(decodedString);
  }

  private encodeURL(object: {
    essentials: boolean;
    statistics?: boolean;
    marketing?: boolean;
  }): string {
    const jsonString = JSON.stringify(object);
    return encodeURIComponent(jsonString);
  }

  private getLanguageDirection(language: string){
    if(language === 'fa'){
      return 'rtl'
    } else {
      return 'ltr'
    }
  }
}
